import { useCallback, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { styled } from '@mui/material'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import Loading from '../../components/process-state-notifications/Loading'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useProgramProjectReportGridOptions } from './gridOptions'
import { useProgramProjectReportData } from './hooks/useProgramProjectReportData'
import { ProgramProjectReportRow } from './model/reportRow'

const ProgramProjectReportPage = ({ uuid }: PageProps) => {
  const gridRef = useRef<AgGridReact<ProgramProjectReportRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi>()
  const gridOptions = useProgramProjectReportGridOptions()
  const { data, loading, fetch } = useProgramProjectReportData()

  const bulkSheetState = useBulkSheetState(uuid, 'ProgramProjectReport')
  const columnState = useColumnState({
    gridApi,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api)
    const columns = event.api.getAllGridColumns() ?? []
    const statusIndex = columns.findIndex(col => col.getColId() === 'status')
    const hasAutoColumn = columns.some(
      col => col.getColId() === 'ag-Grid-AutoColumn'
    )
    if (statusIndex >= 0 && hasAutoColumn) {
      event.api.moveColumns(['ag-Grid-AutoColumn'], statusIndex)
    }
  }, [])

  return (
    <PageArea>
      <Header>
        <ReloadButton onReload={fetch} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
      </Header>
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={data}
        onGridReady={handleGridReady}
        onFilterChanged={columnState.onFilterChanged}
        onSortChanged={columnState.onSortChanged}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export const ProgramProjectReport = projectPrivate(ProgramProjectReportPage)
